import { sendErrorMetric } from "@expert/cloudwatch-metrics";
import { useNotification } from "@expert/common-ui";
import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import { useChatSessionStateStore, useSocketStateStore } from "@simplrsupport/headless-chat-web";
import { useEffect, useMemo } from "react";
import { GuidedFlowMessageMapper, type LogicBuilderGlobalConfig, useSimplrInitializers } from "../logic-builder";
import { useGuideStore } from "../store";
import { useGuideDisclosure } from "./useGuideDisclosure";
import { useParseMessages } from "./useParseMessages";

export interface GuideManagerProps {
    // Session/task
    sessionId: string;
    callSid?: string;
    employeeId?: string;
    isCustomerSession?: boolean;

    // Simplr config
    globalConfig?: LogicBuilderGlobalConfig;

    // Other
    analytics?: Record<string, unknown>;
    clientChannelId?: string;
    logger: Logger;
    partner: Partner;
    requiresTopPadding: boolean;
    sendMessage: SendExpertAssistMessage;
    ssoAccessToken: string;
    taskMdn: string;
}

interface GuideToolboxCommand {
    open: () => void;
    tooltip: string;
    quickActionLabel: string;
    disabled: boolean;
    label: string;
}

export function useGuideManager({
    callSid,
    clientChannelId,
    employeeId,
    globalConfig,
    logger: loggerProp,
    partner,
    requiresTopPadding,
    sendMessage,
    sessionId,
    ssoAccessToken,
    taskMdn,
}: GuideManagerProps) {
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(() => loggerProp.child({ module: "useGuideManager" }), [loggerProp]);

    useEffect(() => {
        logger.info({ sessionId, platformTicketId }, "Solve Guide | Session Updated");
        // oxlint-disable-next-line exhaustive-deps
    }, [sessionId, platformTicketId]);

    useSimplrInitializers({
        globalConfig,
        logger,
        sessionId,
    });

    const { connectionState } = useSocketStateStore();
    const notification = useNotification();

    const {
        analyticsInfo: { currentStepId },
        availableGuides,
        isLoadingSimplrSession,
        setErrorStateSessionId,
        setIsErrorState,
        setIsLoadingSimplrSession,
    } = useGuideStore();

    const areGuidesAvailable = availableGuides.length > 0;

    const { latestMessage, messages } = useParseMessages({ employeeId });
    const { isGuideOpen, openGuide, closeGuide } = useGuideDisclosure({
        callSid,
        logger,
        platformTicketId,
    });

    useEffect(() => {
        if (connectionState === "ticketEnded") {
            logger.info("Solve Guide | Ticket Ended event received");
            setIsErrorState(true);
            setErrorStateSessionId(sessionId);
        } else setIsErrorState(false);
        // oxlint-disable-next-line exhaustive-deps
    }, [connectionState, setErrorStateSessionId, setIsErrorState]);

    useEffect(() => {
        if (platformTicketId && currentStepId !== "" && isLoadingSimplrSession === true) {
            logger.info(
                { currentStepId, platformTicketId },
                "Solve Guide | platformTicketId updated and first step loaded, turning off loading state",
            );
            setIsLoadingSimplrSession(false);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [currentStepId, isLoadingSimplrSession, platformTicketId, setIsLoadingSimplrSession]);

    const ActiveGuide = GuidedFlowMessageMapper({
        areGuidesAvailable,
        callSid,
        clientChannelId,
        latestMessage,
        logger,
        partner,
        onGuideClose: closeGuide,
        opened: isGuideOpen,
        requiresTopPadding,
        sendMessage,
        sessionId,
        ssoAccessToken,
        taskMdn,
    });

    const handleOpenGuideFailure = () => {
        void sendErrorMetric(`openGuideFailure`);
        logger.error(
            { connectionState, globalConfig, messages: JSON.stringify(messages) },
            "Solve guide | No active guide found, trigger error toast",
        );
        notification.error("We're having trouble launching the guide—try again.");
    };

    return {
        areGuidesAvailable,
        guide: ActiveGuide,
        guideToolboxCommand: {
            open: ActiveGuide ? openGuide : handleOpenGuideFailure,
            tooltip: !areGuidesAvailable ? "Coming soon" : undefined,
            quickActionLabel: areGuidesAvailable ? "/g" : undefined,
            disabled: !areGuidesAvailable,
        } as GuideToolboxCommand,
        isGuideOpen,
        closeGuide,
        openGuide,
    };
}
